<script setup lang="ts">
import { storeToRefs } from 'pinia';
import Checkbox from 'primevue/checkbox';
import InputNumber from 'primevue/inputnumber';
import Panel from 'primevue/panel';
import { useShipmentFormAutofillSettingsStore } from '@/stores/useShipmentFormAutofillSettingsStore';

const settings = useShipmentFormAutofillSettingsStore();
const {
  helpFillEnabled,
  showConflictWarnings,
  dwellMinutes,
  maxDrivingHours,
  restHours,
} = storeToRefs(settings);

const autofillOff = () => !helpFillEnabled.value;
</script>

<template>
  <Panel
    header="Stop times (autofill)"
    toggleable
    :collapsed="true"
    class="mt-4 border border-gray-200 bg-white shadow-none"
  >
    <div class="space-y-4 text-sm">
      <p
        class="rounded-md border border-gpx_gray-800 bg-gpx_gray-900 px-3 py-2 text-xs leading-relaxed text-gpx_gray-100"
      >
        Turn this on and we'll suggest times for any empty planned stops,
        using your
        <strong class="font-medium text-gpx_black-200">selected route</strong>
        (segment drive times), your
        <strong class="font-medium text-gpx_black-200">dwell</strong> at stops
        in between, and the
        <strong class="font-medium text-gpx_black-200">driving / rest</strong>
        settings below. Anything you've entered yourself—or that's
        already saved on this shipment—won't get overwritten. If you turn
        on warnings, we may prompt you when your
        <strong class="font-medium text-gpx_black-200">origin departure</strong>
        and
        <strong class="font-medium text-gpx_black-200">destination arrival</strong>
        (both filled in by hand) don't line up with the selected routes and the
        driving / rest settings below.
      </p>

      <div class="flex flex-wrap items-center gap-2">
        <Checkbox
          input-id="wizard-autofill-help"
          binary
          :model-value="helpFillEnabled"
          @update:model-value="settings.setHelpFillEnabled($event)"
        />
        <label
          for="wizard-autofill-help"
          class="cursor-pointer font-medium text-gpx_black-200"
        >
          Help fill in stop times
        </label>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <Checkbox
          input-id="wizard-autofill-warnings"
          binary
          :disabled="autofillOff()"
          :model-value="showConflictWarnings"
          @update:model-value="settings.setShowConflictWarnings($event)"
        />
        <label
          for="wizard-autofill-warnings"
          class="cursor-pointer text-gpx_gray-100"
        >
          Show warnings when times disagree with calculations
        </label>
      </div>

      <div class="grid max-w-md grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label class="mb-1 block text-xs font-medium text-gpx_gray-100">
            Default dwell at middle stops (minutes)
          </label>
          <InputNumber
            :model-value="dwellMinutes"
            :min="1"
            :max="240"
            show-buttons
            button-layout="horizontal"
            size="small"
            class="w-full"
            input-class="w-full"
            :disabled="autofillOff()"
            @update:model-value="settings.setDwellMinutes(Number($event) || 1)"
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-gpx_gray-100">
            Maximum continuous driving before rest (hours)
          </label>
          <InputNumber
            :model-value="maxDrivingHours"
            :min="1"
            :max="24"
            :max-fraction-digits="1"
            :min-fraction-digits="0"
            show-buttons
            button-layout="horizontal"
            size="small"
            class="w-full"
            input-class="w-full"
            :disabled="autofillOff()"
            @update:model-value="
              settings.setMaxDrivingHours(Number($event) || 1)
            "
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-gpx_gray-100">
            Rest duration after max driving (hours)
          </label>
          <InputNumber
            :model-value="restHours"
            :min="1"
            :max="24"
            :max-fraction-digits="1"
            :min-fraction-digits="0"
            show-buttons
            button-layout="horizontal"
            size="small"
            class="w-full"
            input-class="w-full"
            :disabled="autofillOff()"
            @update:model-value="settings.setRestHours(Number($event) || 1)"
          />
        </div>
      </div>
    </div>
  </Panel>
</template>
